import React, { useState, useEffect } from 'react';
import { Link, Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import {
  Bell, LogOut, Sun, Moon, Menu, X, Search,
} from 'lucide-react';
import axios from 'axios';
import TrialBanner from './TrialBanner';
import TermsNotice from './TermsNotice';
import FeedbackButton from './FeedbackButton';
import SupportSessionBanner from './SupportSessionBanner';
import { BottomTabBar } from './BottomTabBar';
import { NAV_GROUPS, canReach } from '../config/navigation';
import GlobalSearch from './GlobalSearch';
import PropertySwitcher from './PropertySwitcher';
import { useIsMobile } from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_BACKEND_URL;


function AppLayout() {
  const { user, logout, token, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(1024);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [alertCount, setAlertCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  // Auto-open sidebar on transition to desktop
  useEffect(() => {
    if (!isMobile && !sidebarOpen) setSidebarOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Auto-close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // CC-3 — the badge counts what needs THIS user's attention.
  //
  // It used to read the whole action queue's `count`: every alert including
  // ones already acknowledged, every unpaid draw, every blocked phase, every
  // delinquent tenant — company-wide, with no filter for the caller, and with
  // tasks missing entirely. That is a number nobody can act on, and a badge
  // users cannot trust is worse than no badge (Michael, §30).
  //
  // APR-11 (Michael, 2026-10-15): the badge is My Approvals' count — the
  // exact same query the tab renders (`/api/approvals/mine`), so "6" means
  // six items this person must approve, authorize or pay right now. No
  // alternate count logic.
  useEffect(() => {
    if (!token) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/approvals/mine`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAlertCount((res.data?.items || []).length);
      } catch { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [token, location.pathname]);

  // SEARCH-3 — ⌘K / Ctrl-K opens global search from anywhere in the app.
  // Bound on the document so it works regardless of what holds focus, but
  // never while the user is typing into a field, where ⌘K may already mean
  // something to that control.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'k' || !(e.metaKey || e.ctrlKey)) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
      e.preventDefault();
      setSearchOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter groups by permissions; drop empty groups entirely so the sidebar stays clean.
  const visibleGroups = NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canReach(item, hasPermission)),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Mobile Overlay - Only show on mobile when sidebar is open */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transition-all duration-300 z-50 flex flex-col ${
          isMobile
            ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') + ' w-64'
            : (sidebarOpen ? 'w-64' : 'w-20 max-sm:w-0 max-sm:-translate-x-full')
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <img src="/northline-logo.png" alt="Northline" className="w-10 h-10 rounded-lg flex-shrink-0 object-cover" />
          {sidebarOpen && (
            <span className="ml-3 text-xl font-bold text-slate-900 dark:text-white tracking-tight">NORTHLINE</span>
          )}
        </div>

        {/* Navigation */}
        <nav
          className="p-3 flex-1 overflow-y-auto"
          data-testid="sidebar-nav"
        >
          {visibleGroups.map((group, gIdx) => (
            <div
              key={group.label}
              className={gIdx === 0 ? '' : 'mt-4'}
              data-testid={`nav-group-${group.label.toLowerCase()}`}
            >
              {sidebarOpen && (
                <div
                  className="px-3 mb-1 text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500 dark:text-slate-400 select-none"
                  data-testid={`nav-group-label-${group.label.toLowerCase()}`}
                >
                  {group.label}
                </div>
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 mb-1 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`
                  }
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="ml-3 font-medium flex-1">{item.name}</span>
                  )}
                  {item.name === 'Command Center' && alertCount > 0 && (
                    <span className="min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 ml-auto">
                      {alertCount > 99 ? '99+' : alertCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${
        isMobile ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-20 max-sm:ml-0')
      }`}>
        {/* Top Bar */}
        <header className="h-14 sm:h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-3 sm:px-6 relative z-50">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              className="text-slate-600 dark:text-slate-400 flex-shrink-0"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <div className="min-w-0">
              {/* Everyone's own profile, two-factor and notifications live on
                  Settings; the sidebar entry is admin-only, so the name is the way in. */}
              <Link to="/settings" title="Your profile and settings" data-testid="topbar-profile-link"
                    className="block hover:underline">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user?.full_name}</h2>
              </Link>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.role_display_name}</p>
            </div>
            {/* SW-1 — hidden on the narrowest screens, where the header has no
                room for it and the Portfolio list is one tap away. */}
            <div className="hidden md:block min-w-0">
              <PropertySwitcher />
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              title="Search (⌘K)"
              className="text-slate-600 dark:text-slate-400"
              data-testid="header-search-button"
            >
              <Search className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/workflow')}
              className="text-slate-600 dark:text-slate-400 relative"
              data-testid="header-alerts-bell"
            >
              <Bell className="w-5 h-5" />
              {alertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {alertCount > 99 ? '99+' : alertCount}
                </span>
              )}
            </Button>
            <FeedbackButton />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              className="text-slate-600 dark:text-slate-400"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-slate-600 dark:text-slate-400"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <SupportSessionBanner />
        <TrialBanner />
        <TermsNotice />
        <main className="p-3 sm:p-6 pb-[calc(56px+env(safe-area-inset-bottom)+1rem)] lg:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom tab bar (admin) */}
      <BottomTabBar
        alertCount={alertCount}
        hasPermission={hasPermission}
        hidden={sidebarOpen && isMobile}
        onMore={() => setSidebarOpen(true)}
      />

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

export default AppLayout;
